import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import CircularProgress from '@/components/CircularProgress'
import { ProtectedAttribute } from '@/components/protected-attribute'
import { ATTRIBUTES } from '@/lib/rbac/constants'
import { getMyClients } from '@/api/sig-data'
import { DIRECTOR_TARGETS_2025, type DirectorTarget } from '@/lib/services/performance.service'
import { formatCurrency } from '@/lib/services/sig-data.service'
import type { ClientResultApiResponse, MyClientResponse } from '@/types/financial'
import { VarianceGauge, ComparativeBarChart, TimelineChart } from '@/lib/components/charts'
import {
  Target,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Building2,
  Users,
  Globe,
  Shield,
  Truck,
  DollarSign,
  BarChart3,
  Award
} from 'lucide-react'

// Type guard to check if response is admin (full data) or director (limited data)
const isAdminResponse = (data: ClientResultApiResponse | MyClientResponse): data is ClientResultApiResponse => {
  return 'receitas' in data
}

export default function MyBusinessTargetsPage() {
  const { user } = useAuth()
  const [clientData, setClientData] = useState<(ClientResultApiResponse | MyClientResponse)[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch client data - backend handles filtering based on user's employeeId
  useEffect(() => {
    const fetchClientData = async () => {
      if (!user?.email) {
        setClientData([])
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)

        // Fetch MY clients - backend filters based on authenticated user
        // Admins get all clients with full data
        // Directors get only their assigned clients with limited fields
        const currentYear = new Date().getFullYear().toString()
        const myClients = await getMyClients(currentYear)

        setClientData(myClients)
      } catch (err) {
        console.error('Error loading client data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load client data')
      } finally {
        setIsLoading(false)
      }
    }

    fetchClientData()
  }, [user?.email])

  // Normalize client data for calculations (works for both admin and director responses)
  const normalizedClients = clientData.map(client => {
    // Director response: fields nested under performance
    const performancePercent = client.proa > 0
      ? Math.round((client.receitas / client.proa) * 100)
      : 0
    return {
      name: client.clientName,
      revenue: client.receitas,
      projected: client.proa,
      performance: performancePercent,
      rawData: client
    }
  })

  // Calculate summary metrics from normalized data
  const totalActualValue = normalizedClients.reduce((sum, client) => sum + client.revenue, 0)
  const totalProaValue = normalizedClients.reduce((sum, client) => sum + client.projected, 0)
  const averagePerformance = normalizedClients.length > 0 
    ? Math.round(normalizedClients.reduce((sum, client) => sum + client.performance, 0) / normalizedClients.length)
    : 0

  // Match with director targets for display purposes (optional context)
  const enhancedClientResults = normalizedClients.map(normalized => {
    const CLIENT_NAME_MAP: Record<string, string> = {
      'BANCO DO BRASIL PROMO': 'BANCO DO BRASIL',
      'BB SEGUROS + COLIGADAS': 'BB CORRETORA DE SEGU',
      'BANCO DO BRASIL DIGITAL': 'BB DIGITAL',
      'SEBRAE': 'SEBRAE',
      'EMBRATUR': 'EMBRATUR AGENCIA BRA',
      'PETROBRAS': 'PETROBRAS',
      'FUNCEF': 'FUNCEF',
      'APEX': 'APEX-BRASIL'
    }

    const matchingTarget = DIRECTOR_TARGETS_2025.find(target => {
      const mappedName = CLIENT_NAME_MAP[target.client]
      return mappedName === normalized.name
    })

    return {
      ...normalized,
      target: matchingTarget,
      fallbackIconComponent: Building2,
      isAdminData: isAdminResponse(normalized.rawData)
    }
  })

  if (!user) {
    return (
      
        <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6 animate-fade-in-up">
          <Shield className="h-16 w-16 text-brand-red animate-pulse" />
          <h1 className="text-3xl font-bold text-white font-display">Acesso Negado</h1>
          <p className="text-brand-gray-light text-center max-w-md font-body text-lg">
            Você precisa estar logado para visualizar suas metas de negócio.
          </p>
        </div>
      
    )
  }

  // Loading state
  if (isLoading) {
    return (
      
        <div className="space-y-8 animate-fade-in-up">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-brand-gray-light font-body">Carregando metas de negócio...</p>
            </div>
          </div>
        </div>
      
    )
  }

  // Error state
  if (error) {
    return (
      
        <div className="space-y-8 animate-fade-in-up">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h3 className="font-display text-lg font-medium text-white mb-2">Erro ao carregar dados</h3>
              <p className="text-brand-gray max-w-md mx-auto mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="btn-primary"
              >
                Tentar Novamente
              </button>
            </div>
          </div>
        </div>
      
    )
  }

  return (
    
      <div className="space-y-8 animate-fade-in-up">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
              Minhas Metas de Negócio
            </h1>
            <p className="text-brand-gray-light font-body text-lg">
              Acompanhe o desempenho dos seus clientes e metas
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="text-sm text-brand-gray font-body bg-brand-dark-light/50 px-4 py-2 rounded-monumenta border border-gray-800/50">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-brand-primary rounded-full animate-pulse"></div>
                <span>Metas 2025 • Última atualização: {new Date().toLocaleString('pt-BR')}</span>
              </div>
            </div>
          </div>
        </div>

        {normalizedClients.length === 0 ? (
          // No clients found
          <div className="card text-center">
            <div className="flex flex-col items-center space-y-6 py-12">
              <Target className="h-16 w-16 text-brand-gray animate-pulse" />
              <div>
                <h3 className="text-2xl font-display font-bold text-white mb-3">
                  Nenhuma Meta Encontrada
                </h3>
                <p className="text-brand-gray-light font-body text-lg max-w-md mx-auto">
                  Não encontramos metas de negócio associadas ao seu usuário. 
                  Entre em contato com a administração se isso for um erro.
                </p>
              </div>
              <div className="bg-brand-dark-light rounded-monumenta p-4 border border-brand-gray/20">
                <p className="text-sm text-brand-gray">
                  <strong>Usuário:</strong> {user.name} ({user.email})
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Summary Metrics */}
            <div className="dashboard-grid lg:grid-cols-4">
              <div className="card-hover">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 rounded-monumenta bg-brand-primary/10">
                    <Target className="w-6 h-6 text-brand-primary" />
                  </div>
                  <span className="text-sm text-brand-gray bg-brand-dark-lighter px-3 py-1 rounded-full">
                    PROA Total
                  </span>
                </div>
                <div className="space-y-2">
                  {/* <ProtectedAttribute
                    attributeName={ATTRIBUTES.CLIENT_FINANCIAL_DATA}
                    action="view"
                    fallback={<div className="text-2xl font-display font-bold text-white">***</div>}
                  > */}
                    <div className="text-2xl font-display font-bold text-white">
                      {formatCurrency(totalProaValue)}
                    </div>
                  {/* </ProtectedAttribute> */}
                  <p className="text-sm text-brand-gray">
                    {normalizedClients.length} cliente{normalizedClients.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              <div className="card-hover">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 rounded-monumenta bg-success/10">
                    <DollarSign className="w-6 h-6 text-success" />
                  </div>
                  <span className="text-sm text-brand-gray bg-brand-dark-lighter px-3 py-1 rounded-full">
                    Realizado
                  </span>
                </div>
                <div className="space-y-2">
                  {/* <ProtectedAttribute
                    attributeName={ATTRIBUTES.CLIENT_FINANCIAL_DATA}
                    action="view"
                    fallback={<div className="text-2xl font-display font-bold text-white">***</div>}
                  > */}
                    <div className="text-2xl font-display font-bold text-white">
                      {formatCurrency(totalActualValue)}
                    </div>
                  {/* </ProtectedAttribute> */}
                  <p className="text-sm text-brand-gray">
                    vs PROA: {formatCurrency(totalProaValue)}
                  </p>
                </div>
              </div>

              <div className="card-hover">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 rounded-monumenta bg-brand-blue/10">
                    <BarChart3 className="w-6 h-6 text-brand-blue" />
                  </div>
                  <span className="text-sm text-brand-gray bg-brand-dark-lighter px-3 py-1 rounded-full">
                    Performance
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="text-2xl font-display font-bold text-white">
                    {averagePerformance}%
                  </div>
                  <p className="text-sm text-brand-gray">
                    Média geral
                  </p>
                </div>
              </div>

              <div className="card-hover">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-monumenta ${
                    totalProaValue > 0 && totalActualValue >= totalProaValue 
                      ? 'bg-success/10' : totalActualValue >= totalProaValue * 0.8 
                      ? 'bg-warning/10' : 'bg-error/10'
                  }`}>
                    <Award className={`w-6 h-6 ${
                      totalProaValue > 0 && totalActualValue >= totalProaValue
                        ? 'text-success' : totalActualValue >= totalProaValue * 0.8
                        ? 'text-warning' : 'text-error'
                    }`} />
                  </div>
                  <span className="text-sm text-brand-gray bg-brand-dark-lighter px-3 py-1 rounded-full">
                    Atingimento
                  </span>
                </div>
                <div className="space-y-2">
                  {/* <ProtectedAttribute
                    attributeName={ATTRIBUTES.CLIENT_FINANCIAL_DATA}
                    action="view"
                    fallback={<div className="text-2xl font-display font-bold text-white">***</div>}
                  > */}
                    <div className="text-2xl font-display font-bold text-white">
                      {totalProaValue > 0 ? Math.round((totalActualValue / totalProaValue) * 100) : 0}%
                    </div>
                  {/* </ProtectedAttribute> */}
                  <p className="text-sm text-brand-gray">
                    da PROA total
                  </p>
                </div>
              </div>
            </div>

            {/* Director Target Attainment Gauges */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card">
                <h3 className="font-display text-xl font-bold text-white mb-4">
                  Atingimento da Meta Principal
                </h3>
                <div className="flex justify-center">
                  <VarianceGauge
                    actual={totalActualValue}
                    target={totalProaValue}
                    label="Meta Principal"
                    size="lg"
                    showPercentage={true}
                  />
                </div>
              </div>

              <div className="card">
                <h3 className="font-display text-xl font-bold text-white mb-4">
                  Projeção de Atingimento
                </h3>
                <div className="space-y-4 p-4">
                  <div className="flex justify-between">
                    <span className="text-brand-gray">Taxa Atual:</span>
                    <span className="text-white font-bold">
                      {totalProaValue > 0 ? ((totalActualValue / totalProaValue) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-brand-gray">Meta Anual:</span>
                    <span className="text-white font-bold">{formatCurrency(totalProaValue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-brand-gray">Realizado:</span>
                    <span className="text-white font-bold">{formatCurrency(totalActualValue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-brand-gray">Gap:</span>
                    <span className={`font-bold ${totalActualValue >= totalProaValue ? 'text-success' : 'text-error'}`}>
                      {formatCurrency(totalActualValue - totalProaValue)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Client Comparison Bar Chart */}
            <div className="card">
              <h3 className="font-display text-xl font-bold text-white mb-4">
                Comparação: Meta vs Realizado
              </h3>
              <ComparativeBarChart
                data={normalizedClients.map(c => ({
                  name: c.name,
                  target: c.projected,
                  actual: c.revenue,
                  variance: c.revenue - c.projected
                }))}
                height={Math.max(400, normalizedClients.length * 60)}
              />
            </div>

            {/* Client Results Grid */}
            <div className="card">
              <h3 className="font-display text-2xl font-bold text-white mb-8 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
                🎯 PROA por Cliente
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {enhancedClientResults.map((client, index) => {
                  const FallbackIcon = client.fallbackIconComponent
                  const isHighPerformance = client.performance >= 80
                  const isTargetMet = client.target && client.revenue >= client.target.targetValue

                  return (
                    <div key={`${client.name}-${index}`} className={`card-interactive group p-6 transition-all duration-200 ${
                      isTargetMet ? 'border-success/30 bg-success/5' : 
                      isHighPerformance ? 'border-brand-primary/30 bg-brand-primary/5' : ''
                    }`}>
                      {/* Header */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-monumenta bg-brand-primary/10 group-hover:bg-brand-primary/20 transition-colors duration-200">
                            <FallbackIcon className="w-6 h-6 text-brand-primary group-hover:scale-110 transition-transform duration-200" />
                          </div>
                          <div>
                            <h3 className="font-display text-lg font-bold text-white group-hover:text-brand-primary transition-colors duration-200">
                              {client.name}
                            </h3>
                          </div>
                        </div>
                        <div className="group-hover:scale-105 transition-transform duration-200">
                          <CircularProgress percentage={Math.floor(client.performance)} />
                        </div>
                      </div>

                        <div className="space-y-4 mt-6">
                          {/* Target Value */}
                          {client.target && (
                            <div className="flex justify-between items-center p-3 bg-brand-dark/30 rounded-monumenta">
                              <span className="text-sm text-brand-gray-light font-body flex items-center gap-2">
                                <Target className="w-4 h-4" />
                                PROA Meta
                              </span>
                              <span className="text-sm text-brand-primary font-display font-bold">
                                {formatCurrency(client.target.targetValue)}
                              </span>
                            </div>
                          )}

                          {/* Realizado */}
                          <div className="flex justify-between items-center p-3 bg-brand-dark/30 rounded-monumenta">
                            <span className="text-sm text-brand-gray-light font-body flex items-center gap-2">
                              <DollarSign className="w-4 h-4" />
                              Realizado
                            </span>
                            <span className="text-sm text-white font-display font-bold">
                              {formatCurrency(client.revenue)}
                            </span>
                          </div>
                        </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </div>
    
  )
}
